<template>
  <div class="view-box columns bg-white border-bottom">
    <settings-header
      button-route="new"
      :header-title="titlePage"
      :is-ticket="isTicket"
      show-back-button
      :back-button-label="$t('CONTACT_PROFILE.BACK_BUTTON')"
      :back-url="backUrl"
      :show-new-button="false"
      @close-modal-ticket="closeModalTicket"
    >
      <div>
        <thumbnail
          :src="contact.thumbnail"
          :username="contact.name"
          size="32px"
          class="margin-right-1"
        />
      </div>
      <template v-if="isTicket" v-slot:extra-actions>
        <div style="display:flex;align-items:center;gap:6px;">
          <ticket-transfer-dropdown
            ref="transferDropdown"
            :ticket="ticket"
            @open="
              closeFollowersDropdown();
              closeKebab();
            "
            @success="onTransferSuccess"
          />

          <!-- Followers (mocked) -->
          <div
            class="header-followers"
            v-click-outside="closeFollowersDropdown"
          >
            <button class="followers-btn" @click="toggleFollowersDropdown">
              <fluent-icon icon="people-team" size="15" />
              <span>{{ mockedFollowers.length }} {{ followersLabel }}</span>
              <fluent-icon icon="chevron-down" size="12" />
            </button>
            <div
              v-if="showFollowersDropdown"
              class="ticket-header-dropdown ticket-header-dropdown--followers"
            >
              <div class="ticket-dropdown-section-title">
                {{ $t('TICKET.HEADER.ADD_FOLLOWER') }}
              </div>
              <div class="ticket-dropdown-search">
                <input
                  v-model="followerSearch"
                  type="text"
                  :placeholder="$t('TICKET.HEADER.SEARCH_AGENT')"
                  @click.stop
                />
              </div>
              <ul class="ticket-dropdown-list">
                <li
                  v-for="agent in filteredFollowerAgents"
                  :key="agent.id"
                  @click="toggleFollower(agent)"
                >
                  <thumbnail
                    :src="agent.thumbnail"
                    :username="agent.name"
                    size="20px"
                  />
                  <span>{{ agent.name }}</span>
                  <fluent-icon
                    v-if="isFollower(agent)"
                    icon="checkmark"
                    size="14"
                  />
                </li>
                <li v-if="filteredFollowerAgents.length === 0" class="empty">
                  {{ $t('TICKET.HEADER.NO_AGENTS') }}
                </li>
              </ul>
              <div
                v-if="mockedFollowers.length > 0"
                class="ticket-followers-chips"
              >
                <span v-for="f in mockedFollowers" :key="f.id" class="chip">
                  <thumbnail
                    :src="f.thumbnail"
                    :username="f.name"
                    size="18px"
                  />
                  <span>{{ f.name }}</span>
                  <button @click.stop="removeFollower(f)">×</button>
                </span>
              </div>
            </div>
          </div>

          <!-- Win button -->
          <button
            v-if="showWinLoseButtons"
            class="action-btn action-btn--win"
            @click="handleWinTicket"
          >
            {{ $t('TICKET.WIN.BUTTON') }}
          </button>

          <!-- Lose button -->
          <button
            v-if="showWinLoseButtons"
            class="action-btn action-btn--lose"
            @click="toggleLoseModal"
          >
            {{ $t('TICKET.LOSE.BUTTON') }}
          </button>

          <!-- Reopen button -->
          <button
            v-if="showReopenButton"
            class="action-btn action-btn--reopen"
            @click="handleReopenTicket"
          >
            {{ $t('TICKET.REOPEN.TOOLTIP') }}
          </button>

          <!-- Kebab menu -->
          <div class="header-kebab" v-click-outside="closeKebab">
            <button class="kebab-btn" @click="toggleKebab">
              <fluent-icon icon="more-horizontal" size="18" />
            </button>
            <div
              v-if="showKebab"
              class="ticket-header-dropdown ticket-header-dropdown--kebab"
            >
              <ul class="ticket-dropdown-list">
                <li @click="onEditTicket">
                  <fluent-icon icon="edit" size="14" />
                  <span>{{ $t('CRM_MGMT.FORM.EDIT') }}</span>
                </li>
                <li @click="onShareTicket">
                  <fluent-icon icon="share" size="14" />
                  <span>{{ $t('TICKET.SHARE.LABEL') }}</span>
                </li>
                <li class="danger" @click="onDeleteTicket">
                  <fluent-icon icon="delete" size="14" />
                  <span>{{ $t('TICKET.DELETE.LABEL') }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </template>
    </settings-header>

    <!-- Stage timeline -->
    <ticket-stage-timeline v-if="isTicket" :ticket="ticket" />

    <div
      v-if="uiFlags.isFetchingItem"
      class="text-center p-normal fs-default h-full"
    >
      <spinner size="" />
      <span>{{ $t('CONTACT_PROFILE.LOADING') }}</span>
    </div>
    <div
      v-else-if="contact.id"
      class="overflow-hidden column contact--dashboard-content"
    >
      <div class="row h-full">
        <contact-info-panel
          :show-close-button="false"
          :show-avatar="false"
          :contact="contact"
          :ticket-selected="ticket"
          :is-ticket="isTicket"
          :hide-ticket-actions="isTicket"
          @close-modal-ticket="closeModalTicket"
          @updateTicket="updateTicket"
          @open-schedule-communication-tab="openScheduleCommunicationTab"
          @ticket-contacts-updated="onTicketContactsUpdated"
        />
        <div class="small-12 medium-9 h-full">
          <woot-tabs :index="selectedTabIndex" @change="onClickTabChange">
            <woot-tabs-item
              v-for="tab in tabs"
              :key="tab.key"
              :name="tab.name"
              :show-badge="false"
            />
          </woot-tabs>
          <div class="tab-content overflow-auto">
            <contact-notes
              v-if="selectedTabKey === 'notes'"
              :contact-id="Number(contactId)"
              :ticket="ticket"
              :is-ticket="isTicket"
              @created="refreshLogs"
            />

            <tasks-view
              v-if="selectedTabKey === 'tasks'"
              :contact-id="Number(contactId)"
              :ticket="ticket"
              :is-ticket="isTicket"
              @created="refreshLogs"
            />

            <schedule-message-view
              v-if="
                selectedTabKey === 'communications' && canShowCommunicationTab
              "
              :ticket="ticket"
              :contact="contact"
              :target-contact="scheduleTargetContact"
              :ticket-contacts-refresh-key="ticketContactsRefreshKey"
              @clear-target-contact="onCommunicationSuccess"
            />

            <div v-if="isTicket">
              <div class="history-section-header">
                <span class="history-section-title">{{
                  $t('TICKET_LOGS.TITLE')
                }}</span>
              </div>
              <ticket-logs-view
                ref="ticketLogsView"
                :key="`ticket-logs-${ticket.id}`"
                :ticket-id="ticket.id"
                :contact-id="Number(contactId)"
                :ticket-name="`#${ticket.id} - ${ticket.text}`"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals triggered from TicketCrmHeader -->
    <modal-update-ticket
      v-if="showUpdateTicketModal && isTicket"
      :ticket="ticket"
      :show="showUpdateTicketModal"
      @cancel="toggleUpdateTicketModal"
      @updateTicket="updateTicket"
    />

    <woot-delete-modal
      v-if="showDeleteTicketModal && isTicket"
      :show.sync="showDeleteTicketModal"
      :on-close="toggleDeleteTicketModal"
      :on-confirm="confirmDeletionTicket"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="`#${ticket.id} ${ticket.text}?`"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />

    <modal-lose-ticket
      v-if="showLoseModal && isTicket"
      :ticket="ticket"
      :show="showLoseModal"
      @cancel="toggleLoseModal"
      @success="handleLoseSuccess"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ContactInfoPanel from '../components/ContactInfoPanel.vue';
import ContactNotes from 'dashboard/modules/notes/NotesOnContactPage';
import SettingsHeader from '../../settings/SettingsHeader.vue';
import Spinner from 'shared/components/Spinner';
import Thumbnail from 'dashboard/components/widgets/Thumbnail';
import TasksView from '../../crm/components/Tasks/TasksView.vue';
import ScheduleMessageView from '../../crm/components/Communications/ScheduleMessageView.vue';
import TicketLogsView from '../../../../components/TicketLogsView.vue';
import TicketTransferDropdown from '../components/TicketTransferDropdown.vue';
import TicketStageTimeline from '../components/TicketStageTimeline.vue';
import { mixin as clickaway, directive as clickOutside } from 'vue-clickaway';
import ModalUpdateTicket from '../../conversation/contact/ModalUpdateTicket.vue';
import ModalLoseTicket from '../../conversation/contact/ModalLoseTicket.vue';
import alertMixin from 'shared/mixins/alertMixin';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { frontendURL } from '../../../../helper/URLHelper';

export default {
  components: {
    ContactInfoPanel,
    ContactNotes,
    SettingsHeader,
    Spinner,
    Thumbnail,
    TasksView,
    TicketLogsView,
    ScheduleMessageView,
    TicketStageTimeline,
    ModalUpdateTicket,
    ModalLoseTicket,
    TicketTransferDropdown,
  },
  directives: {
    'click-outside': clickOutside,
  },
  mixins: [clickaway, alertMixin],
  props: {
    contactId: {
      type: [String, Number],
      required: true,
    },
    ticketSelected: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      selectedTabKey: 'notes',
      ticket: this.ticketSelected,
      scheduleCommunicationContact: null,
      ticketContactsRefreshKey: 0,
      showUpdateTicketModal: false,
      showDeleteTicketModal: false,
      showLoseModal: false,
      showFollowersDropdown: false,
      showKebab: false,
      followerSearch: '',
      mockedFollowers: [],
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      agentList: 'agents/getAgents',
      kanbanList: 'getKanbanfunnels',
      accountId: 'getCurrentAccountId',
    }),
    kanban() {
      if (!this.ticket?.kanbanfunnel_id) return null;
      const fid = String(this.ticket.kanbanfunnel_id);
      return this.kanbanList.find(k => String(k.id) === fid) || null;
    },
    isBusinessOpportunity() {
      if (!this.kanban) return false;
      return (
        this.kanban.type_opportunity === 'business' ||
        this.kanban.type_opportunity === 'both'
      );
    },
    followersLabel() {
      return this.mockedFollowers.length === 1
        ? this.$t('TICKET.HEADER.FOLLOWER_SINGULAR')
        : this.$t('TICKET.HEADER.FOLLOWER_PLURAL');
    },
    showWinLoseButtons() {
      const status = this.ticket?.status || 'open';
      return (
        this.isBusinessOpportunity && status !== 'gain' && status !== 'lose'
      );
    },
    showReopenButton() {
      const status = this.ticket?.status || 'open';
      return status !== 'open';
    },
    filteredFollowerAgents() {
      const q = (this.followerSearch || '').toLowerCase();
      return this.agentList.filter(a => a.name.toLowerCase().includes(q));
    },

    isTicket() {
      return !!this.ticket?.id;
    },
    titlePage() {
      if (!this.isTicket) {
        return this.contact.name;
      }
      return `#${this.ticket.id} - ${this.ticket.text}`;
    },
    canShowCommunicationTab() {
      return this.isTicket;
    },
    scheduleTargetContact() {
      return (
        this.scheduleCommunicationContact ||
        this.ticket?.contact ||
        this.contact ||
        {}
      );
    },
    selectedTabIndex() {
      const index = this.tabs.findIndex(tab => tab.key === this.selectedTabKey);
      return index >= 0 ? index : 0;
    },
    logsTabIndex() {
      return this.canShowCommunicationTab ? 3 : 2;
    },
    tabs() {
      const tabsDefault = [
        {
          key: 'notes',
          name: this.$t('NOTES.HEADER.TITLE'),
        },
      ];

      if (this.isTicket) {
        tabsDefault.push({
          key: 'tasks',
          name: this.$t('CRM_MGMT.TASK.TITLE'),
        });

        if (this.canShowCommunicationTab) {
          tabsDefault.push({
            key: 'communications',
            name: this.$t('TICKET.FORM.COMMUNICATIONS.SCHEDULE_TAB'),
          });
        }
      }

      return tabsDefault;
    },
    showEmptySearchResult() {
      const hasEmptyResults = !!this.searchQuery && this.records.length === 0;
      return hasEmptyResults;
    },
    contact() {
      return this.$store.getters['contacts/getContact'](this.contactId);
    },
    backUrl() {
      return `/app/accounts/${this.$route.params.accountId}/contacts`;
    },
  },
  watch: {
    contactId: {
      immediate: true,
      handler() {
        this.fetchContactDetails();
      },
    },
    ticketSelected: {
      immediate: true,
      handler(ticket) {
        this.ticket = ticket || {};
        this.scheduleCommunicationContact = null;

        if (ticket?.tab === 1) {
          this.selectedTabKey = 'tasks';
          return;
        }

        if (ticket?.tab === 2 || ticket?.tab === 3) {
          this.selectedTabKey = 'logs';
          return;
        }

        this.selectedTabKey = 'notes';
      },
    },
  },
  mounted() {
    this.fetchContactDetails();

    if (this.ticketSelected?.tab === 1) {
      this.selectedTabKey = 'tasks';
    }

    if (this.ticketSelected?.tab === 2 || this.ticketSelected?.tab === 3) {
      this.selectedTabKey = 'logs';
    }
  },
  methods: {
    toggleFollowersDropdown() {
      this.showFollowersDropdown = !this.showFollowersDropdown;
      this.showKebab = false;
      this.$refs.transferDropdown?.closeDropdown();
    },
    closeFollowersDropdown() {
      this.showFollowersDropdown = false;
    },
    toggleKebab() {
      this.showKebab = !this.showKebab;
      this.showFollowersDropdown = false;
      this.$refs.transferDropdown?.closeDropdown();
    },
    closeKebab() {
      this.showKebab = false;
    },
    isFollower(agent) {
      return this.mockedFollowers.some(f => f.id === agent.id);
    },
    toggleFollower(agent) {
      if (this.isFollower(agent)) {
        this.removeFollower(agent);
      } else {
        this.mockedFollowers = [...this.mockedFollowers, agent];
      }
    },
    removeFollower(agent) {
      this.mockedFollowers = this.mockedFollowers.filter(
        f => f.id !== agent.id
      );
    },
    onTransferSuccess(updatedTicket) {
      if (updatedTicket?.id) {
        const currentKanbanId = Number(this.ticket?.kanbanfunnel_id);
        const newKanbanId = Number(updatedTicket?.kanbanfunnel_id);

        if (newKanbanId !== currentKanbanId) {
          this.$store.dispatch('removeTicketFromState', updatedTicket.id);
        } else {
          this.$store.dispatch('updateTicket', updatedTicket);
        }

        this.ticket = { ...this.ticket, ...updatedTicket };
        this.$emit('updateTicket', this.ticket);
      }
      this.$emit('close-modal-ticket', false);
      this.closeModalTicket(false);
    },
    onEditTicket() {
      this.showKebab = false;
      this.toggleUpdateTicketModal();
    },
    async onShareTicket() {
      this.showKebab = false;
      if (!this.ticket?.id) return;

      const ticketUrl =
        window.chatwootConfig.hostURL +
        frontendURL(
          `accounts/${this.accountId}/crm/tickets/${this.ticket.id}`
        );
      await copyTextToClipboard(ticketUrl);
      this.showAlert(this.$t('TICKET.SHARE.SUCCESS'));
    },
    onDeleteTicket() {
      this.showKebab = false;
      this.toggleDeleteTicketModal();
    },
    handleWinTicket() {
      const payload = { ...this.ticket, status: 'gain' };
      this.$store.dispatch('updateTicket', payload).then(() => {
        this.ticket = { ...this.ticket, status: 'gain' };
        const msg = this.isBusinessOpportunity
          ? this.$t('TICKET.WIN.BUSINESS_SUCCESS')
          : this.$t('TICKET.WIN.SUCCESS');
        this.showAlert(msg);
        this.$emit('updateTicket', this.ticket);
        this.$emit('close-modal-ticket', false);
        this.closeModalTicket(false);
      });
    },
    handleReopenTicket() {
      const payload = { ...this.ticket, status: 'open' };
      this.$store.dispatch('updateTicket', payload).then(() => {
        this.ticket = { ...this.ticket, status: 'open' };
        const msg = this.isBusinessOpportunity
          ? this.$t('TICKET.REOPEN.BUSINESS_SUCCESS')
          : this.$t('TICKET.REOPEN.SUCCESS');
        this.showAlert(msg);
        this.$emit('updateTicket', this.ticket);
        this.$emit('close-modal-ticket', 'reopen');
        this.closeModalTicket(false);
      });
    },
    handleLoseSuccess() {
      this.showLoseModal = false;
      this.ticket = { ...this.ticket, status: 'lose' };
      this.$emit('updateTicket', this.ticket);
      this.$emit('close-modal-ticket', false);
      this.closeModalTicket(false);
    },
    toggleLoseModal() {
      this.showLoseModal = !this.showLoseModal;
    },
    toggleUpdateTicketModal() {
      this.showUpdateTicketModal = !this.showUpdateTicketModal;
    },
    toggleDeleteTicketModal() {
      this.showDeleteTicketModal = !this.showDeleteTicketModal;
    },
    confirmDeletionTicket() {
      const payload = { ...this.ticket, status: 'deleted' };
      this.$store
        .dispatch('updateTicket', payload)
        .then(() => {
          this.showDeleteTicketModal = false;
          this.showAlert(this.$t('TICKET.DELETE.API.SUCCESS_MESSAGE'));
          this.$emit('close-modal-ticket', true);
          this.closeModalTicket(true);
        })
        .catch(error => {
          this.showAlert(
            error?.message || this.$t('TICKET.DELETE.API.ERROR_MESSAGE')
          );
        });
    },
    openScheduleCommunicationTab(contact) {
      this.scheduleCommunicationContact = contact || null;
      if (this.canShowCommunicationTab) {
        this.selectedTabKey = 'communications';
      }
    },
    onCommunicationSuccess() {
      this.clearScheduleCommunicationContact();
      this.refreshLogs();
    },
    onTicketContactsUpdated() {
      this.ticketContactsRefreshKey += 1;
    },
    clearScheduleCommunicationContact() {
      this.scheduleCommunicationContact = null;
    },
    updateTicket(updatedTicket) {
      this.ticket = { ...this.ticket, ...updatedTicket };
      this.$emit('updateTicket', updatedTicket);
    },
    closeModalTicket(isDelete) {
      this.$emit('close-modal-ticket', isDelete);
    },
    onClickTabChange(index) {
      this.selectedTabKey = this.tabs[index]?.key || 'notes';
    },
    refreshLogs() {
      this.$refs.ticketLogsView?.fetchLogs();
    },
    fetchContactDetails() {
      const { contactId: id } = this;
      this.$store.dispatch('contacts/show', { id });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/mixins';

.left {
  border-right: 1px solid var(--color-border);
  overflow: auto;
}

.right {
  padding: var(--space-normal);
}

.tab-content {
  background: var(--color-background-light);
  height: calc(100% - 40px);
  padding: var(--space-normal);
}

.border-bottom {
  border-bottom: 1px solid var(--s-50);
}
</style>

<style lang="scss">
/* ── Ticket header slot styles (non-scoped to work inside slots) ── */
.header-followers,
.header-kebab {
  position: relative;
  flex-shrink: 0;
}

button.followers-btn {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-normal);
  background: var(--s-25);
  cursor: pointer;
  font-size: var(--font-size-small);
  line-height: 1;
  color: var(--s-700);
  white-space: nowrap;

  &:hover {
    background: var(--s-75);
  }
}

button.kebab-btn {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-normal);
  background: var(--s-25);
  cursor: pointer;
  color: var(--s-700);

  &:hover {
    background: var(--s-75);
  }
}

button.action-btn {
  box-sizing: border-box;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 14px;
  border-radius: var(--border-radius-normal);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  border: none;
  line-height: 1;
  transition: filter 0.15s;

  &:hover {
    filter: brightness(0.92);
  }

  &--win {
    background: #28a745;
    color: #fff;
  }

  &--lose {
    background: #dc3545;
    color: #fff;
    border: none;
  }

  &--reopen {
    background: #fff;
    color: var(--w-600);
    border: 1px solid var(--w-400);
  }
}

.ticket-header-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 220px;
  background: var(--white);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-normal);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 9999;

  &--followers {
    left: 0;
    right: auto;
    min-width: 260px;
  }

  &--kebab {
    min-width: 160px;
  }
}

.ticket-dropdown-section-title {
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  color: var(--s-500);
  padding: 4px 12px;
  border-bottom: 1px solid var(--s-75);
}

.ticket-dropdown-search {
  padding: 6px 12px;
  border-bottom: 1px solid var(--s-75);

  input {
    width: 100%;
    font-size: var(--font-size-small);
    border: 1px solid var(--s-100);
    border-radius: var(--border-radius-small);
    padding: 4px 8px;
    outline: none;

    &:focus {
      border-color: var(--w-500);
    }
  }
}

.ticket-dropdown-list {
  list-style: none;
  margin: 0;
  padding: 4px 0;
  max-height: 200px;
  overflow-y: auto;

  li {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    cursor: pointer;
    font-size: var(--font-size-small);
    color: var(--s-800);

    &:hover {
      background: var(--s-25);
    }
    &.danger {
      color: var(--r-500);
    }
    &.empty {
      color: var(--s-400);
      cursor: default;
    }
  }
}

.ticket-followers-chips {
  border-top: 1px solid var(--s-75);
  padding: 6px 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: var(--s-75);
    border-radius: 12px;
    padding: 2px 8px 2px 4px;
    font-size: var(--font-size-mini);

    button {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--s-500);
      font-size: 14px;
      line-height: 1;
      padding: 0;
    }
  }
}

.history-section-header {
  display: flex;
  align-items: center;
  padding: var(--space-small) 0 var(--space-small);
  border-bottom: 1px solid var(--color-border);
  margin-bottom: var(--space-normal);
}

.history-section-title {
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--color-body-light);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
</style>
